// Auth-flow guards — single source of truth for:
//   1. The PUBLIC_ROUTES set (consumed by AuthGate + __root's isPublicPath).
//   2. The once-only mount-guard hook used by every step of the login flow.
//
// Why a shared hook: setAccess() (called on successful MFA verify) nulls
// flowSession synchronously, so a useEffect that bounces to /login when
// !flowSession would race the explicit navigate-to-/terminal — and the
// bounce wins. The fix is to evaluate the bounce condition exactly once at
// mount, capturing initial state into a ref, and trust the page's onSubmit
// for every subsequent transition.

import { type NavigateOptions, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

/** Routes that don't require authentication. Edit ONE place to change them. */
const PUBLIC_ROUTES: ReadonlySet<string> = new Set([
  '/', // marketing
  '/login',
  '/login/password-change',
  '/login/mfa-enroll',
  '/login/mfa-verify',
  '/login/recovery-codes',
]);

export function isPublicPath(pathname: string): boolean {
  return PUBLIC_ROUTES.has(pathname);
}

/**
 * Bounce to `fallback` if `predicate()` was false at mount time. Predicate is
 * captured into a ref ONCE — never re-evaluated, never re-navigates. After
 * mount, the page's own handlers own all navigation.
 *
 * Usage:
 *   useOnceMountGuard(
 *     () => useAuth.getState().flowSession != null,
 *     { to: "/login" },
 *   );
 */
export function useOnceMountGuard(predicate: () => boolean, fallback: NavigateOptions): void {
  const navigate = useNavigate();
  // Initial-state-only ref. `useRef` initializer runs once per mount; the
  // boolean is captured and never reads from the live store again.
  const initialOk = useRef<boolean>(predicate());
  // navigate is intentionally omitted — TSR returns a new fn per render and
  // including it would re-fire the effect.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialOk.current) void navigate(fallback);
  }, []);
}
